/**
 * Checkout Page
 * Multi-step checkout flow
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe, Stripe as StripeType } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import api from '../../../api/client';
import { StripePaymentForm } from '../../../components/payment/StripePaymentForm';
import { PayPalButton } from '../../../components/payment/PayPalButton';
import type { Cart } from '@phoenixbill/shared/types/cart';
import type { PaymentGatewayConfig } from '@phoenixbill/shared/types/payment-gateway';

type CheckoutStep = 'review' | 'billing' | 'payment' | 'confirm';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('review');
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Form data
  const [billingAddressSameAsProfile, setBillingAddressSameAsProfile] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'US',
  });
  const [notes, setNotes] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Payment state
  const [gateways, setGateways] = useState<PaymentGatewayConfig[]>([]);
  const [selectedGateway, setSelectedGateway] = useState<string>('');
  const [stripePromise, setStripePromise] = useState<Promise<StripeType | null> | null>(null);
  const [paypalClientId, setPaypalClientId] = useState<string>('');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [orderId, setOrderId] = useState<number | null>(null);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);

  useEffect(() => {
    loadCart();
    loadPaymentGateways();
  }, []);

  // Load Stripe/PayPal when gateway is selected
  useEffect(() => {
    if (selectedGateway === 'stripe') {
      const stripeConfig = gateways.find((g) => g.type === 'stripe');
      if (stripeConfig?.config?.publishableKey) {
        setStripePromise(loadStripe(stripeConfig.config.publishableKey));
      }
    } else if (selectedGateway === 'paypal') {
      const paypalConfig = gateways.find((g) => g.type === 'paypal');
      if (paypalConfig?.config?.clientId) {
        setPaypalClientId(paypalConfig.config.clientId);
      }
    }
  }, [selectedGateway, gateways]);

  const loadCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/client/cart');
      const cartData = response.data.data;

      if (!cartData || cartData.items.length === 0) {
        navigate('/client/cart');
        return;
      }

      setCart(cartData);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentGateways = async () => {
    try {
      const response = await api.get('/payment/gateways');
      setGateways(response.gateways);
      if (response.gateways.length > 0) {
        setSelectedGateway(response.gateways[0].type);
      }
    } catch (err: any) {
      console.error('Failed to load payment gateways:', err);
    }
  };

  const handleSubmitOrder = async () => {
    if (!acceptTerms) {
      setError('You must accept the terms of service');
      return;
    }

    if (!selectedGateway) {
      setError('Please select a payment method');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const orderData: any = {
        billingAddressSameAsProfile,
        notes,
        acceptTerms,
      };

      if (!billingAddressSameAsProfile) {
        orderData.billingAddress = billingAddress;
      }

      // Create order first
      const response = await api.post('/client/orders', orderData);
      const order = response.data.data;

      setOrderId(order.id);
      setInvoiceId(order.invoiceId);

      // If manual payment, just redirect to success
      if (selectedGateway === 'manual') {
        navigate(`/client/orders/${order.id}/success`);
        return;
      }

      // For Stripe or PayPal, initialize payment
      if (selectedGateway === 'stripe') {
        await initializeStripePayment(order);
      } else if (selectedGateway === 'paypal') {
        // PayPal button will handle payment
        setSubmitting(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create order');
      setSubmitting(false);
    }
  };

  const initializeStripePayment = async (order: any) => {
    try {
      // Create payment intent for the invoice
      const paymentResponse = await api.post('/payment/process', {
        invoiceId: order.invoiceId,
        amount: order.total,
        gateway: 'stripe',
        paymentMethod: 'card',
      });

      setClientSecret(paymentResponse.clientSecret);
      setSubmitting(false);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to initialize payment');
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = () => {
    if (orderId) {
      navigate(`/client/orders/${orderId}/success`);
    }
  };

  const handlePaymentError = (error: string) => {
    setError(error);
    setSubmitting(false);
  };

  const formatPrice = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const steps = [
    { id: 'review', label: 'Review Cart', icon: '🛒' },
    { id: 'billing', label: 'Billing Info', icon: '📝' },
    { id: 'payment', label: 'Payment', icon: '💳' },
    { id: 'confirm', label: 'Confirm', icon: '✓' },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!cart) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-100 mb-8">Checkout</h1>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                    index <= currentStepIndex
                      ? 'bg-orange-500 text-white'
                      : 'bg-slate-700/50 text-slate-400'
                  }`}
                >
                  {step.icon}
                </div>
                <p
                  className={`text-sm mt-2 ${
                    index <= currentStepIndex ? 'text-gray-100' : 'text-gray-500'
                  }`}
                >
                  {step.label}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`h-1 flex-1 mx-2 ${
                    index < currentStepIndex ? 'bg-orange-500' : 'bg-slate-700/50'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Step Content */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
        {currentStep === 'review' && (
          <ReviewStep cart={cart} formatPrice={formatPrice} />
        )}

        {currentStep === 'billing' && (
          <BillingStep
            billingAddressSameAsProfile={billingAddressSameAsProfile}
            setBillingAddressSameAsProfile={setBillingAddressSameAsProfile}
            billingAddress={billingAddress}
            setBillingAddress={setBillingAddress}
          />
        )}

        {currentStep === 'payment' && (
          <PaymentStep
            gateways={gateways}
            selectedGateway={selectedGateway}
            setSelectedGateway={setSelectedGateway}
          />
        )}

        {currentStep === 'confirm' && (
          <ConfirmStep
            cart={cart}
            notes={notes}
            setNotes={setNotes}
            acceptTerms={acceptTerms}
            setAcceptTerms={setAcceptTerms}
            formatPrice={formatPrice}
            orderId={orderId}
            invoiceId={invoiceId}
            selectedGateway={selectedGateway}
            stripePromise={stripePromise}
            clientSecret={clientSecret}
            paypalClientId={paypalClientId}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => {
            if (currentStep === 'review') {
              navigate('/client/cart');
            } else {
              const prevIndex = currentStepIndex - 1;
              setCurrentStep(steps[prevIndex].id as CheckoutStep);
            }
          }}
          className="px-6 py-2 bg-slate-700/50 text-gray-200 rounded-lg hover:bg-slate-600/50 transition-colors"
        >
          ← Back
        </button>

        {currentStep !== 'confirm' ? (
          <button
            onClick={() => {
              const nextIndex = currentStepIndex + 1;
              if (currentStep === 'billing' && !billingAddressSameAsProfile) {
                // Validate billing address
                if (!billingAddress.address1 || !billingAddress.city || !billingAddress.postcode) {
                  setError('Please fill in all required billing address fields');
                  return;
                }
              }
              setError('');
              setCurrentStep(steps[nextIndex].id as CheckoutStep);
            }}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Continue →
          </button>
        ) : (
          <>
            {/* Show Place Order button only if order hasn't been created yet, or if payment form is shown for Stripe/PayPal */}
            {!orderId || selectedGateway === 'manual' ? (
              <button
                onClick={handleSubmitOrder}
                disabled={submitting || !acceptTerms}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Processing...' : selectedGateway === 'manual' ? 'Place Order' : 'Place Order & Pay'}
              </button>
            ) : null}
            {/* Payment buttons are inside the StripePaymentForm and PayPalButton components */}
          </>
        )}
      </div>
    </div>
  );
}

// Review Step
function ReviewStep({ cart, formatPrice }: { cart: Cart; formatPrice: (n: number) => string }) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-100 mb-4">Review Your Order</h2>
      <div className="space-y-4">
        {cart.items.map(item => (
          <div key={item.id} className="flex justify-between items-start border-b border-slate-700/50 pb-4">
            <div className="flex-1">
              <h3 className="font-medium text-gray-100">{item.productName}</h3>
              <p className="text-sm text-slate-400">
                {item.billingCycle} × {item.quantity}
              </p>
              {item.domain && <p className="text-sm text-gray-500">Domain: {item.domain}</p>}
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-100">{formatPrice(item.subtotal + item.totalSetupFee)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Billing Step
function BillingStep({ billingAddressSameAsProfile, setBillingAddressSameAsProfile, billingAddress, setBillingAddress }: any) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-100 mb-4">Billing Information</h2>

      <div className="mb-4">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={billingAddressSameAsProfile}
            onChange={e => setBillingAddressSameAsProfile(e.target.checked)}
            className="rounded bg-slate-900 border-slate-700/50 text-orange-500 focus:ring-orange-500"
          />
          <span className="text-slate-300">Use my profile address</span>
        </label>
      </div>

      {!billingAddressSameAsProfile && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Address Line 1 *</label>
            <input
              type="text"
              value={billingAddress.address1}
              onChange={e => setBillingAddress({ ...billingAddress, address1: e.target.value })}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Address Line 2</label>
            <input
              type="text"
              value={billingAddress.address2}
              onChange={e => setBillingAddress({ ...billingAddress, address2: e.target.value })}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">City *</label>
              <input
                type="text"
                value={billingAddress.city}
                onChange={e => setBillingAddress({ ...billingAddress, city: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">State/Province</label>
              <input
                type="text"
                value={billingAddress.state}
                onChange={e => setBillingAddress({ ...billingAddress, state: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Postal Code *</label>
              <input
                type="text"
                value={billingAddress.postcode}
                onChange={e => setBillingAddress({ ...billingAddress, postcode: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Country *</label>
              <select
                value={billingAddress.country}
                onChange={e => setBillingAddress({ ...billingAddress, country: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Payment Step
function PaymentStep({ gateways, selectedGateway, setSelectedGateway }: {
  gateways: PaymentGatewayConfig[];
  selectedGateway: string;
  setSelectedGateway: (gateway: string) => void;
}) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-100 mb-4">Payment Method</h2>

      {gateways.length === 0 ? (
        <div className="text-slate-400 text-sm">No payment methods available</div>
      ) : (
        <div className="space-y-3">
          {gateways.map((gateway) => (
            <div
              key={gateway.type}
              className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                selectedGateway === gateway.type
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-slate-700/50 hover:border-slate-600'
              }`}
              onClick={() => setSelectedGateway(gateway.type)}
            >
              <div className="flex items-center">
                <input
                  type="radio"
                  checked={selectedGateway === gateway.type}
                  onChange={() => setSelectedGateway(gateway.type)}
                  className="mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium">{gateway.name}</div>
                  {gateway.description && (
                    <div className="text-sm text-slate-400 mt-1">{gateway.description}</div>
                  )}
                </div>
                {gateway.type === 'stripe' && <div className="text-2xl">💳</div>}
                {gateway.type === 'paypal' && <div className="text-2xl">🅿️</div>}
                {gateway.type === 'manual' && <div className="text-2xl">🏦</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedGateway === 'manual' && (
        <div className="mt-4 bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 text-sm text-yellow-300">
          <p className="font-medium mb-2">Manual Payment</p>
          <p>Your order will be created with "unpaid" status. Please contact support to arrange payment.</p>
        </div>
      )}
    </div>
  );
}

// Confirm Step
function ConfirmStep({
  cart,
  notes,
  setNotes,
  acceptTerms,
  setAcceptTerms,
  formatPrice,
  orderId,
  invoiceId,
  selectedGateway,
  stripePromise,
  clientSecret,
  paypalClientId,
  onPaymentSuccess,
  onPaymentError,
}: any) {
  // If order is created and payment gateway requires payment form, show it
  const showPaymentForm = orderId && (selectedGateway === 'stripe' || selectedGateway === 'paypal');

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-100 mb-4">
        {showPaymentForm ? 'Complete Payment' : 'Review & Confirm'}
      </h2>

      {!showPaymentForm && (
        <>
          {/* Order Summary */}
          <div className="bg-slate-900 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-100 mb-3">Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>Subtotal</span>
                <span>{formatPrice(cart.subtotal)}</span>
              </div>
              {cart.setupFeeTotal > 0 && (
                <div className="flex justify-between text-slate-300">
                  <span>Setup Fees</span>
                  <span>{formatPrice(cart.setupFeeTotal)}</span>
                </div>
              )}
              {cart.discount > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Discount</span>
                  <span>-{formatPrice(cart.discount)}</span>
                </div>
              )}
              {cart.taxAmount > 0 && (
                <div className="flex justify-between text-slate-300">
                  <span>Tax</span>
                  <span>{formatPrice(cart.taxAmount)}</span>
                </div>
              )}
              <div className="border-t border-slate-700/50 pt-2">
                <div className="flex justify-between text-lg font-semibold text-gray-100">
                  <span>Total</span>
                  <span>{formatPrice(cart.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Notes */}
          <div className="mb-6">
            <label className="block text-sm text-slate-400 mb-1">Order Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Any special instructions or notes..."
            />
          </div>

          {/* Terms */}
          <div className="mb-6">
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={e => setAcceptTerms(e.target.checked)}
                className="mt-1 rounded bg-slate-900 border-slate-700/50 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-slate-300 text-sm">
                I have read and agree to the{' '}
                <a href="/terms" target="_blank" className="text-orange-400 hover:underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" className="text-orange-400 hover:underline">
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>
        </>
      )}

      {/* Payment Forms */}
      {showPaymentForm && selectedGateway === 'stripe' && clientSecret && stripePromise && (
        <div className="space-y-4">
          <p className="text-slate-400 text-sm mb-4">
            Your order has been created. Complete payment to activate your services.
          </p>
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'night',
                variables: {
                  colorPrimary: '#3b82f6',
                  colorBackground: '#1f2937',
                  colorText: '#f3f4f6',
                  colorDanger: '#ef4444',
                  fontFamily: 'system-ui, sans-serif',
                  borderRadius: '0.5rem',
                },
              },
            }}
          >
            <StripePaymentForm
              amount={cart.total}
              currency={cart.currency || 'USD'}
              invoiceId={invoiceId}
              onSuccess={onPaymentSuccess}
              onError={onPaymentError}
              onCancel={() => {}}
            />
          </Elements>
        </div>
      )}

      {showPaymentForm && selectedGateway === 'paypal' && paypalClientId && (
        <div className="space-y-4">
          <p className="text-slate-400 text-sm mb-4">
            Your order has been created. Complete payment with PayPal to activate your services.
          </p>
          <PayPalScriptProvider
            options={{
              clientId: paypalClientId,
              currency: cart.currency || 'USD',
              intent: 'capture',
            }}
          >
            <PayPalButton
              amount={cart.total}
              currency={cart.currency || 'USD'}
              invoiceId={invoiceId}
              onSuccess={onPaymentSuccess}
              onError={onPaymentError}
              onCancel={() => {}}
            />
          </PayPalScriptProvider>
        </div>
      )}
    </div>
  );
}
